/**
 * Speaker grid filtering.
 *
 * Every card is already in the DOM, so filtering is a visibility toggle — no
 * fetch, no spinner, no request race. Correct up to roughly 150 speakers, which
 * is the agreed ceiling; past that this becomes a REST-backed query.
 */

const DEBOUNCE_MS = 220;

// Counter behind each card's view-transition-name, which has to be unique across
// the whole document rather than per grid.
let instance = 0;

/**
 * Filter one grid and announce the result.
 *
 * @param {HTMLElement} grid    The block root.
 * @param {Object}      state   Current selection.
 * @param {boolean}     [announce] Whether to write the count into the live
 *                                 region. False on the initial call so nothing
 *                                 is announced before the user has interacted;
 *                                 the rest of apply() still runs so the
 *                                 starting visibility state is set correctly.
 */
function apply( grid, state, announce = true ) {
	const cards = grid.querySelectorAll( '.dn-speaker-card' );
	const term = state.search.trim().toLowerCase();
	let shown = 0;

	cards.forEach( ( card ) => {
		// Pills and search are AND: a card must satisfy both to survive.
		const topicOk =
			state.topic === 'all' ||
			card.dataset.topics.split( ' ' ).includes( state.topic );
		const searchOk = ! term || card.dataset.search.includes( term );
		const visible = topicOk && searchOk;

		card.hidden = ! visible;
		if ( visible ) {
			shown += 1;
		}
	} );

	const empty = grid.querySelector( '.dn-speaker-grid__empty' );
	if ( empty ) {
		empty.hidden = shown !== 0;
	}

	const status = grid.querySelector( '.dn-speaker-grid__status' );
	if ( status && announce ) {
		status.textContent = `${ shown } ${ 1 === shown ? 'speaker' : 'speakers' }`;
	}

	grid.classList.toggle(
		'dn-speaker-grid--filtered',
		state.topic !== 'all' || '' !== term
	);

	// Opt-in per placement: only carousels whose editor toggle is on disappear
	// while a filter is active.
	grid.querySelectorAll( '.dn-carousel--hide-when-filtered' ).forEach(
		( carousel ) => {
			carousel.hidden = state.topic !== 'all' || '' !== term;
		}
	);
}

/**
 * Wire one grid.
 *
 * @param {HTMLElement} grid The block root.
 */
function initGrid( grid ) {
	if ( grid.dataset.dnFilterReady ) {
		return;
	}
	grid.dataset.dnFilterReady = 'true';

	const bar = grid.querySelector( '.dn-speaker-grid__filters' );
	if ( ! bar ) {
		return;
	}

	// Rendered hidden so that without JS no dead controls are offered.
	bar.hidden = false;

	const state = { topic: 'all', search: '' };
	const pills = grid.querySelectorAll( '.dn-speaker-grid__pill' );
	const cards = grid.querySelectorAll( '.dn-speaker-card' );

	/*
	 * Name every card so the browser can animate it.
	 *
	 * A view transition tweens between two snapshots, and it can only match an
	 * element across them if that element has a unique view-transition-name. Name
	 * them and the browser does both halves of the effect for free: the filtered
	 * card fades out, and every card that shifts up to close the gap slides
	 * rather than jumps. Naming is done here rather than in the markup because
	 * the value must be unique per document, and the same speaker can legitimately
	 * appear in two grids on one page.
	 */
	cards.forEach( ( card, i ) => {
		card.style.viewTransitionName = `dn-speaker-${ instance }-${ i }`;
	} );
	instance += 1;

	/**
	 * Filter, animated where the browser and the visitor allow it.
	 *
	 * @param {Object} next Selection to apply.
	 */
	const transition = ( next ) => {
		if (
			! document.startViewTransition ||
			window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
		) {
			apply( grid, next );
			return;
		}
		document.startViewTransition( () => apply( grid, next ) );
	};

	pills.forEach( ( pill ) => {
		pill.addEventListener( 'click', () => {
			state.topic = pill.dataset.topic;
			pills.forEach( ( other ) => {
				other.setAttribute(
					'aria-pressed',
					String( other === pill )
				);
			} );
			transition( state );
		} );
	} );

	/*
	 * Typing animates too, on the same path as the pills.
	 *
	 * Safe because the work is debounced: the filter runs once the visitor
	 * pauses, not once per keystroke, so there is at most one transition per
	 * pause rather than a queue of aborted ones. The debounce is 220ms rather
	 * than 150 for exactly that reason — 150 is inside normal typing cadence,
	 * which would start a transition between characters and immediately skip it.
	 */
	const search = grid.querySelector( '.dn-speaker-grid__search' );
	if ( search ) {
		let timer;
		search.addEventListener( 'input', () => {
			window.clearTimeout( timer );
			timer = window.setTimeout( () => {
				state.search = search.value;
				transition( state );
			}, DEBOUNCE_MS );
		} );
	}

	// Sets the starting visibility state without announcing a count that no
	// interaction has caused yet.
	apply( grid, state, false );
}

function init() {
	document.querySelectorAll( '.dn-speaker-grid' ).forEach( initGrid );
}

if ( 'loading' === document.readyState ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}

export { apply, initGrid };
